import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTimezoneSelect, allTimezones } from 'react-timezone-select';
import debounce from 'lodash.debounce';
// components
import AuthComponent from '../../components/AuthComponent';
import BSEntAuth from '../../components/BSEntAuth';
// import GenExistingUser from '../../components/AuthComponent/GenExistingUser';
import LoadingSpinner from '../../components/LoadingSpinner';

// utils & API
import { useAuth } from '../../utils/Auth';
import { useOrganization } from '../../utils/OrganizationContext';
import { useQuery } from '../../utils/hooks/useQuery';
import API from '../../API';
import styles from './AuthPage.module.css';

// helper from signup
const createQrTemplate = ({ nickname, category, created_at, qr_style, id, redirect_url, org_id }) => {
  if (!qr_style || !Object?.keys(qr_style)?.length || !qr_style?.data || !id) throw ('incomplete');

  return {
    active: true,
    alternate_redirect_url: [redirect_url],
    category: category || null,
    created_at: created_at,
    microsite: '',
    nickname: nickname || id,
    org_id: org_id,
    qr_style: qr_style,
    qr_type: 'link',
    redirect_url: redirect_url,
    schedule: null,
    tags: [],
    updated_at: Date.now()
  };
};

// main page
export default function AuthPage() {
  // initialization & params
  const { create, login, currentUser, isLoading, refreshUserToken, setActiveOrgId, userObj, setIsRegistering } = useAuth();
  const { refreshOrganizations } = useOrganization();
  const navigate = useNavigate();
  const { addUIDtoDB, getGeneratorQr, addQrFromGenerator } = API;
  const [searchParams] = useSearchParams();
  const query = window.location.search;
  const queryHook = useQuery();

  // determines mode: use url param 'mode' to switch
  const mode = searchParams.get('mode') === 'login' ? 'login' : 'signup';

  // params specific to signup/redirects
  const gen_id = searchParams.get('gen_id');
  const signupType = gen_id ? 'generator' : searchParams.get('t');
  const bsEnt = searchParams.get('ent');
  const redirect = useMemo(() => searchParams.get('r'), [searchParams]);
  const ref = queryHook.get('ref');

  // state initializations
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(false);
  const [postSignupPath, setPostSignupPath] = useState(null);
  const [signupState, setSignupState] = useState({
    qrGenObj: { qr_style: {}, created_at: 0, id: '', nickname: '', category: '' },
    error: '',
    loading: mode === 'signup' && !!gen_id
  });
  const [timezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [formattedTimezone, setFormattedTimezone] = useState({});
  const { options } = useTimezoneSelect('original', allTimezones);

  // invite-related state
  const inviteToken = searchParams.get('token');
  const [inviteData, setInviteData] = useState(null);
  const [inviteError, setInviteError] = useState('');
  // end state initializations

  // redirect helper login/signup
  const toggleModeRedirect = useCallback((targetMode) => {
    const params = new URLSearchParams(searchParams);
    params.set('mode', targetMode);
    navigate(`/auth?${params.toString()}`, { replace: true });
  }, [navigate, searchParams]);

  // data fetches and effects
  // fetch invitation data when token is present
  useEffect(() => {
    const fetchInviteData = async () => {
      if (!inviteToken) return;

      try {
        const response = await API.getInviteData(inviteToken);

        // Check if invitation is expired
        if (response.data.expiresAt < Date.now() || (response.data.invalidated_at !== null && response.data.invalidated_at < Date.now())) {
          setInviteError('This invitation link has expired.');
          return;
        }

        // Check if invitation has been used already
        if (response.data.accepted_at !== null && Number(response.data.accepted_at || 0) > 0) {
          setInviteError('This invitation link has already been used.');
          return;
        }

        setInviteData(response.data);
      } catch (err) {
        console.error('Error fetching invite data:', err);
        setInviteError('Invalid or expired invitation link.');
      }
    };

    fetchInviteData();
  }, [inviteToken]);

  // timezone effect (signup)
  useEffect(() => {
    if (timezone) {
      options.forEach(option => {
        if (option.value === timezone) {
          setFormattedTimezone({ label: option.label, offset: option.offset, value: option.value })
        };
      });
    };
  }, [timezone, options]);

  // generator qr fetch (signup)
  const handleGetGeneratorQr = useCallback(async () => {
    try {
      const qrObj = await getGeneratorQr(gen_id);
      const redirect_url = qrObj.qr_style.data;

      qrObj.qr_style.data = `https://lqr.ai/${gen_id}`
      qrObj.qr_style.type = 'svg';
      qrObj.qr_style.width = 128;
      qrObj.qr_style.height = 128;
      qrObj.qr_style.image = qrObj.qr_style.image ? `${qrObj.qr_style.image}?${Date.now()}` : '';
      qrObj.qr_style.imageOptions.imageSize = 0.4;

      setSignupState({
        qrGenObj: { qr_style: qrObj.qr_style, id: qrObj.id, created_at: qrObj.created_at, redirect_url: redirect_url, nickname: qrObj.id, category: '' },
        error: '',
        loading: false
      });
    } catch (err) {
      if (err === 'Does not exist') {
        setSignupState(prev => ({ ...prev, error: 'No QR', loading: false }))
      } else {
        setSignupState(prev => ({ ...prev, error: 'Error retrieving QR.', loading: false }))
      };
    };
  }, [gen_id, getGeneratorQr]);

  useEffect(() => {
    if (mode === 'signup' && gen_id) {
      handleGetGeneratorQr();
    };
  }, [mode, gen_id, handleGetGeneratorQr]);

  // auth functions
  // signup
  const handleSignUp = async () => {
    setLoginError(false);
	setIsRegistering(true);

    try {
      const userCredentials = await create(email, password);
      const uid = userCredentials.user.uid;

      const data = {
        categories: [],
        plan: {
          analytics: 0,
          categories: 3,
          liquid_qrs: 3,
          plan_id: 0,
          support: 0,
          team_size: 1,
          widgets: 0
        },
        sonar_length: 3600000,
        team: [uid],
        timezone: formattedTimezone,
        ...(inviteData && { org_id: inviteData.org_id, invite_token: inviteToken })
      };

      await addUIDtoDB(data)
	  await refreshUserToken()

	  await new Promise(resolve => setTimeout(resolve, 1500));

      if (gen_id) {
        const genQr = createQrTemplate({ ...signupState.qrGenObj, org_id: uid });
        await addQrFromGenerator({ id: gen_id, data: genQr });
      };

	  setIsRegistering(false);

      if (!inviteToken) {
        // Defer redirect until auth context reflects the new user
        const targetPath = query ? `/upgrade${query}` : !inviteData ? '/home' : '';
        setPostSignupPath(targetPath);
      }
    } catch (err) {
		setIsRegistering(false);
      	console.error('Signup Error:', err);
    };
  };

  // login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(false);
	setIsRegistering(true);

    try {
      await login(email, password);
      // If there's an invite, the authenticated user invite UI will show
      // User must manually click Accept/Decline button
	  await new Promise(resolve => setTimeout(resolve, 800));
    } catch (err) {
      setLoginError(true);
      console.error(err);
    } finally {
		setIsRegistering(false);
	}
  };

  // handle authenticated user accepting invite
  const handleAuthenticatedUserAcceptInvite = async () => {
    try {
      // Check if user is already a member of this organization
      const currentOrgIds = userObj?.org_ids || [];
      if (currentOrgIds.includes(inviteData.org_id)) {
        setInviteError('You are already a member of this organization.');
        return;
      }

      await API.acceptOrgInvite(inviteToken);
      await refreshUserToken();

      // Ensure personal org (currentUser.uid) and invited org are included
      // Use Set to deduplicate, then ensure personal org and invited org are included
      const updatedOrgIds = [...new Set([currentUser.uid, ...currentOrgIds, inviteData.org_id])];
      await refreshOrganizations(updatedOrgIds);

      setActiveOrgId(inviteData.org_id);
      navigate('/organizations');
    } catch (err) {
      console.error('Error accepting invite:', err);
      // Only set error message if not already a member error
      if (!inviteError) {
        setInviteError('Failed to accept organization invitation. Please try again.');
      }
    }
  };

  // useEffect for user already logged in (adapted)
  useEffect(() => {
    const handleDebounceNav = debounce((path, opt = {}) => {
      navigate(path, opt);
    }, 250, { trailing: true });

    if (isLoading) return;
    // If user is authenticated with a valid invite, don't redirect - show invite UI
    if (currentUser && inviteToken && inviteData) {
      return () => handleDebounceNav.cancel();
    }

    if (currentUser) {
      // priority => generator/edit > specific redirect > home
      if (redirect === 'edit' || ref === 'bs') {
        handleDebounceNav(`/collection/edit${window.location.search}`, { replace: true });
      } else if (!redirect) {
        handleDebounceNav('/home');
      } else {
        handleDebounceNav(`/${redirect}${window.location.search}` || '/home', { replace: true });
      };
    };

    return () => handleDebounceNav.cancel();
  }, [currentUser, isLoading, navigate, redirect, ref, signupType, inviteToken, inviteData]);

  // post-signup navigation once auth context has the new user
  useEffect(() => {
    if (!postSignupPath) return;
    if (isLoading) return;
    if (!currentUser) return;

    navigate(postSignupPath, { replace: true });
    setPostSignupPath(null);
  }, [postSignupPath, isLoading, currentUser, navigate]);

  // bsEntAuth check combined
  // should happen before the main loading and rendering
  const isBSEntAuthFlow = useMemo(() => {
    if (mode === 'login' && searchParams.get('e') && searchParams.get('p') && searchParams.get('tag')) {
      return {
        type: 'login',
        email: searchParams.get('e'),
        password: searchParams.get('p'),
        tag: searchParams.get('tag'),
        ent: searchParams.get('ent'),
      };
    } else if (mode === 'signup' && signupType === 'bsent') {
      return {
        type: 'signup',
        email: searchParams.get('email'),
        ent: bsEnt,
      };
    }
    return null;
  }, [mode, searchParams, signupType, bsEnt]);

  if (isBSEntAuthFlow) {
    return <BSEntAuth {...isBSEntAuthFlow} />
  };

  const renderAuthComponent = () => {

    // states for clarity
    const isSignupGeneratorFlow = mode === 'signup' && signupType === 'generator';
    const isQrFound = !!signupState?.qrGenObj?.id;

    // show invite error
    if (inviteError) {
      return (
        <div className={styles.error}>
          <p>{inviteError}</p>
          <button onClick={() => {
            setInviteError('');
            navigate('/auth?mode=login');
          }}>Back to Login</button>
        </div>
      );
    }

    // authenticated user with valid invite - show accept invite UI
    if (currentUser && inviteToken && inviteData) {
      return (
        <div className={styles.card}>
          <h2 className={styles.header}>Accept Organization Invite</h2>
          <p style={{ textAlign: 'center', marginBottom: '1.5rem' }}>You're invited to join an organization on LiquidQR.</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              className={styles.primaryBtn}
              onClick={handleAuthenticatedUserAcceptInvite}
            >
              Accept Invite
            </button>
            <button
              className={styles.secondaryBtn}
              onClick={() => navigate('/home')}
            >
              Decline
            </button>
          </div>
        </div>
      );
    }

    // currently in generator flow AND still loading
    if (isSignupGeneratorFlow && signupState.loading) {
      return <LoadingSpinner />;
    }

    // special case - generator qr not found
    if (isSignupGeneratorFlow && !signupState.loading && !isQrFound) {
      return (
        <div className={styles.nope}>
          This QR does not exist or has already been converted. Head to your collection to view your QRs.
          <button onClick={() => navigate('/collection')}>Collection</button>
        </div>
      );
    }

    // special case - existing user on a generator flow
    if (currentUser && isSignupGeneratorFlow && isQrFound) {
      return <LoadingSpinner />
    }

    // standard signup
    return (
      <AuthComponent
        mode={mode}
        signupType={signupType}
        // mode toggle
        toggleModeRedirect={toggleModeRedirect}
        // form fields
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        // auth handlers
        handleSignUp={handleSignUp}
        handleLogin={handleLogin}
        // state/feedback
        loginError={loginError}
        setLoginError={setLoginError}
        gen_id={isSignupGeneratorFlow ? gen_id : null}
        signupState={isSignupGeneratorFlow ? signupState : null}
        setSignupState={isSignupGeneratorFlow ? setSignupState : null}
        // invite data
        inviteData={inviteData}
        inviteError={inviteError}
      />
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        {renderAuthComponent()}
      </div>
    </div>
  )
};